import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { nameBasedProtectedRoutes } from "../../../router/protected";
import Loader from '../../../components/common/Loader';
import { Card } from "react-bootstrap";
import MaterialReactTable, {
  type MRT_ColumnDef,
  type MRT_SortingState,
} from "material-react-table";
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { datetimeFormats } from "../../../constants/constants";
import moment from "moment";
import { previousInspectorInspectionRequest, exportPreviousInspectorInspectionRequest } from "../../../services/dashboard";
import { getEncodedValue } from "../../../helpers/common";
import { GOOGLE_MAP_URL } from "../../../constants/paths";
import Pagination from "@mui/material/Pagination";
import FormSelect from "components/common/FormSelect";
import InputSelect from "components/common/InputSelect";
import MonthPicker from "components/common/MonthPicker";

/**
 * Create a custom component which will render upcoming inspection section in dashboard.
 * @param props
 * @returns Today's Inspection component
 */
export const PreviousInspectionModule = (props: { userId: number }) => {
  const PAGE_NAME = "PREVIOUS_INSPECTIONS";
  const getSavedState = () => {
    const saved = localStorage.getItem(PAGE_NAME);
    return saved ? JSON.parse(saved) : {};
  };
  const savedState = getSavedState();
  const [sortColumn, setSortColumn] = useState("inspection_date");
  const [sortDirection, setSortDirection] = useState("asc");
  const [fromRows, setFromRows] = useState(0);
  const [toRows, setToRows] = useState(0);
  const [totalRow, setTotalRow] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(savedState?.date ? new Date(savedState.date) : new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: sortColumn, desc: false },
  ]); // The default sorting data to show status column sort ico
  const [data, setData] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  useEffect(() => {
    fetchUpcomingInspections({ page: currentPage, date: selectedDate });
     
  }, [sorting, selectedDate]);
  const handlePageChange = (event, page) => {
    setCurrentPage(page);

    fetchUpcomingInspections({ page: page, date: selectedDate });
  };

  const handleMonthChange = (date) => {
    setSelectedDate(date);
    fetchUpcomingInspections({ page: 1, date: selectedDate });
  };
  const MonthArray = () => {
    const months = [];

    for (let i = 1; i <= 12; i++) {
      const monthName = new Date(0, i - 1).toLocaleString("default", {
        month: "long",
      });
      months.push({ key: i, value: monthName });
    }

    return months;
  };

  const fetchUpcomingInspections = async (params) => {
    const param = {
      sortColumn: 0 in sorting ? sorting[0].id : sortColumn,
      sortDirection:
        0 in sorting
          ? sorting[0].desc === false
            ? "asc"
            : "desc"
          : sortDirection,
      ...params,
    };
    previousInspectorInspectionRequest(param, props.userId).then((response) => {
      setData(response.inspectionResult);
      setTotalPages(response.totalPages);
      setTotalRow(response.total);
      const fromRowsVal =
        response.inspectionResult.length > 0
          ? (response.page - 1) * response.limit + 1
          : 0;
      let toRowsVal = fromRowsVal + response.inspectionResult.length - 1;
      toRowsVal = toRowsVal > 0 ? toRowsVal : 0;
      setFromRows(fromRowsVal);
      setToRows(toRowsVal);
    });
  };

  /* Column Definition requires for MaterialReactTable */
  type UpcommingInspection = {
    id: string;
    sr_number: string;
    address: string;
    city: string;
    zipcode: string;
    user: {
      full_name: string;
      company_name: string;
    };
    state: {
      title: string;
    };
    inspection_date: string;
    job_time: string;
    status: string;
  };

  // Mentioning table columns belows.
  const tableColumns = useMemo<MRT_ColumnDef<UpcommingInspection>[]>(
    () => [
      {
        accessorKey: "sr_number",
        header: "SR NO.",
        enableSorting: true,
        Cell: ({ cell, row }) => (
          <Link
            to={`${
              nameBasedProtectedRoutes.iInspectionDetail.path
            }/${getEncodedValue(row.original.id)}`}
            className="text-decoration-none text-link"
          >
            <strong>{cell.getValue<string>()}</strong>
          </Link>
        ),
      },
      {
        accessorKey: "user.company_name",
        enableSorting: true,
        header: "COMPANY",
      },
      {
        accessorKey: "user.full_name",
        enableSorting: true,

        header: "CUSTOMER",
      },
      {
        accessorKey: "address",
        header: "LOCATION",
        enableSorting: true,

        Cell: ({ cell, row }) => {
          const city = row.original?.city ? ", " + row.original?.city : "";
          const state = row.original?.state
            ? ", " + row.original?.state?.title
            : "";
          const zip = row.original?.zipcode ? ", " + row.original?.zipcode : "";
          const fullAddress = row.original.address + city + state + zip;
          return row.original.address ? (
            <Link
              target="_blank"
              to={`${GOOGLE_MAP_URL}${fullAddress}`}
              className="text-decoration-none text-link"
            >
              {fullAddress}
            </Link>
          ) : (
            ""
          );
        },
      },
      {
        accessorKey: "inspection_date",
        header: "INSPECTION DATE",
        enableSorting: true,
        minWidth: "30%",
        muiTableHeadCellProps: () => ({ className: "text-nowrap" }),
        Cell: ({ cell, row }) => {
          return row.original.status !== "pending"
            ? moment(
                `${row.original.inspection_date} ${row.original.job_time}`
              ).format(datetimeFormats.separatedDateTime)
            : "-";
        },
      },
    ],
    []
  );
  const handleSorting =(a) =>{
    console.log("aa,", a);
  }

  const downloadPreviousInspections = async () => {
    const param = {
      sortColumn: 0 in sorting ? sorting[0].id : sortColumn,
      sortDirection:
        0 in sorting
          ? sorting[0].desc === false
            ? "asc"
            : "desc"
          : sortDirection,
      date: selectedDate,
    };
    setIsLoading(true);
    exportPreviousInspectorInspectionRequest(param, props.userId).then((response) => {
      if (response) {
        window.open(response)
      }
      /* const blob = new Blob([response], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'previous_inspections_Report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url); */
    }).catch((error) => {

    }).finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <div className="col-xl-12 mb-3">
      <Loader isLoading={isLoading} />
      <Card.Header className="border-0 bg-default bg-theme black-color fs-20 fw-bold ls-2 br-0 pe-4 ps-4 pt-11 d-flex justify-content-between align-items-center">
        PREVIOUS INSPECTIONS 
        <div className="d-flex justify-content-between align-items-center">
          <span title="Download Report">
            <SimCardDownloadIcon 
              className="cursor-pointer me-3 download-icon" 
              onClick={downloadPreviousInspections}
            />
          </span>
          <label className="min-width-104 ">View By</label>
          <div className="min-width-104 selectDashboard ">
            {/* <InputSelect
              controlId="viewby"
              label=""
              placeholder="view by"
              touched=""
              handleBlur={(e) => {}}
              handleChange={(e) => {
                handleMonthChange(e.target.value);
              }}
              errorsField="Fsdf"
              value={selectedMonth}
              options={MonthArray()}
            /> */}
            <MonthPicker
              selectedDate={selectedDate}
              isClearable={false}
              onChange={(date) => {
                if (date) {
                  setSelectedDate(date)
                  localStorage.setItem(PAGE_NAME, JSON.stringify({date: date}));
                 // handleMonthChange(date);
                }
              }}
            />
          </div>
        </div>
      </Card.Header>
      <Card.Body className="br-10 p-4">
        <div className="table-responsive ">
          <MaterialReactTable
            muiTableProps={{
              classes: {
                root: "table custom-table border-none-table table-striped machine-custome-table bills-table",
              },
            }}
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            enableDensityToggle={false}
            enableHiding={false}
            enableColumnFilters={false}
            enablePagination={false}
            enableSorting={true}
            columns={tableColumns}
            data={data}
            onSortingChange={setSorting}
            manualSorting
            state={{ sorting }}
            initialState={{ pagination: { pageSize: 5, pageIndex: 2 } }}
          />

          {totalPages > 0 ? (
            <div className="paggination">
              <p className="showing-text">
                Showing {fromRows}-{toRows} of {totalRow}
              </p>
              <Pagination
                count={totalPages}
                page={currentPage}
                shape="rounded"
                variant="outlined"
                onChange={handlePageChange}
              />
            </div>
          ) : (
            ""
          )}
        </div>
      </Card.Body>
    </div>
  );
};
